import React, { useState, useEffect, useRef } from "react";
import { TextField, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  dateFieldRoot: {
    "& .MuiInputBase-input": {
      color: theme.palette?.type === "dark" ? "#ffffff !important" : "var(--hrms-text-strong, #1f2937) !important",
      "-webkit-text-fill-color": theme.palette?.type === "dark" ? "#ffffff !important" : "var(--hrms-text-strong, #1f2937) !important",
    },
    "& .MuiInputBase-input::placeholder": {
      opacity: "1 !important",
      color: theme.palette?.type === "dark" ? "rgba(255, 255, 255, 0.7) !important" : "var(--hrms-text-strong, #1f2937) !important",
      "-webkit-text-fill-color": theme.palette?.type === "dark" ? "rgba(255, 255, 255, 0.7) !important" : "var(--hrms-text-strong, #1f2937) !important",
      fontFamily: "inherit !important",
      fontSize: "1rem !important",
      fontWeight: "400 !important",
    },
    "& .MuiInputBase-input::-webkit-input-placeholder": {
      opacity: "1 !important",
      color: theme.palette?.type === "dark" ? "rgba(255, 255, 255, 0.7) !important" : "var(--hrms-text-strong, #1f2937) !important",
      "-webkit-text-fill-color": theme.palette?.type === "dark" ? "rgba(255, 255, 255, 0.7) !important" : "var(--hrms-text-strong, #1f2937) !important",
      fontFamily: "inherit !important",
      fontSize: "1rem !important",
      fontWeight: "400 !important",
    },
  },
}));

// Strict parser for display in DD/MM/YYYY
export const getDisplayDate = (val) => {
  if (!val) return "";
  const rawVal = typeof val === "object" && val !== null && val.target ? val.target.value : val;
  if (!rawVal || typeof rawVal !== "string") return "";
  const trimmed = rawVal.trim().split(" ")[0].split("T")[0];
  if (!trimmed) return "";

  // Check strict format YYYY-MM-DD
  const mYmd = moment(trimmed, "YYYY-MM-DD", true);
  if (mYmd.isValid()) {
    return mYmd.format("DD/MM/YYYY");
  }

  // Check strict format DD/MM/YYYY
  const mDmy = moment(trimmed, "DD/MM/YYYY", true);
  if (mDmy.isValid()) {
    return mDmy.format("DD/MM/YYYY");
  }

  // Check strict format DD-MM-YYYY
  const mDmyDash = moment(trimmed, "DD-MM-YYYY", true);
  if (mDmyDash.isValid()) {
    return mDmyDash.format("DD/MM/YYYY");
  }

  return "";
};

// Strict parser for YYYY-MM-DD (backend/database format)
export const getYmdDate = (val) => {
  if (!val) return "";
  const rawVal = typeof val === "object" && val !== null && val.target ? val.target.value : val;
  if (!rawVal || typeof rawVal !== "string") return "";
  const trimmed = rawVal.trim().split(" ")[0].split("T")[0];
  if (!trimmed) return "";

  const mYmd = moment(trimmed, "YYYY-MM-DD", true);
  if (mYmd.isValid()) {
    return mYmd.format("YYYY-MM-DD");
  }

  const mDmy = moment(trimmed, "DD/MM/YYYY", true);
  if (mDmy.isValid()) {
    return mDmy.format("YYYY-MM-DD");
  }

  const mDmyDash = moment(trimmed, "DD-MM-YYYY", true);
  if (mDmyDash.isValid()) {
    return mDmyDash.format("YYYY-MM-DD");
  }

  return "";
};

// Safe, intuitive DD/MM/YYYY formatting helper
export const formatDdMmYyyy = (raw, isDeleting = false) => {
  if (!raw) return "";
  const trimmed = String(raw).trim();

  // If user pasted ISO date YYYY-MM-DD
  if (/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(trimmed)) {
    const parts = trimmed.split(/[-/]/);
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }

  // If user pasted 8 consecutive digits without separators (e.g. 08042026)
  const onlyDigits = trimmed.replace(/\D/g, "");
  if (!trimmed.includes("/") && onlyDigits.length === 8) {
    return `${onlyDigits.slice(0, 2)}/${onlyDigits.slice(2, 4)}/${onlyDigits.slice(4, 8)}`;
  }

  // If deleting and ends with slash, drop the slash
  let workingRaw = trimmed;
  if (isDeleting && workingRaw.endsWith("/")) {
    workingRaw = workingRaw.slice(0, -1);
  }

  const parts = workingRaw.split("/");
  let day = (parts[0] || "").replace(/\D/g, "").slice(0, 2);
  let month = (parts[1] !== undefined ? parts[1] : "").replace(/\D/g, "").slice(0, 2);
  let year = (parts[2] !== undefined ? parts[2] : "").replace(/\D/g, "").slice(0, 4);

  // Validate day: auto-pad if single digit > 3
  if (day.length === 1 && parseInt(day, 10) > 3) {
    day = "0" + day;
  } else if (day.length === 2) {
    let dNum = parseInt(day, 10);
    if (dNum === 0) dNum = 1;
    if (dNum > 31) dNum = 31;
    day = String(dNum).padStart(2, "0");
  }

  // Validate month: auto-pad if single digit > 1
  if (month.length === 1 && parseInt(month, 10) > 1) {
    month = "0" + month;
  } else if (month.length === 2) {
    let mNum = parseInt(month, 10);
    if (mNum === 0) mNum = 1;
    if (mNum > 12) mNum = 12;
    month = String(mNum).padStart(2, "0");
  }

  let result = day;
  const shouldSlashDay = parts.length > 1 || (day.length === 2 && !isDeleting);
  if (shouldSlashDay) {
    result += "/" + month;
  }
  const shouldSlashMonth = parts.length > 2 || (month.length === 2 && parts.length > 1 && !isDeleting);
  if (shouldSlashMonth) {
    result += "/" + year;
  }

  if (isDeleting && result.endsWith("/")) {
    result = result.replace(/\/+$/, "");
  }

  return result;
};

const DdMmYyyyDateField = ({
  id,
  name,
  label,
  value,
  placeholder,
  onChange,
  error,
  helperText,
  min,
  max,
  fullWidth = true,
  size = "small",
  variant = "outlined",
  disabled = false,
  required = false,
  className = "",
  style = {},
  InputLabelProps = {},
  InputProps = {},
  inputProps = {},
  margin,
  FormHelperTextProps,
}) => {
  const classes = useStyles();
  const inputRef = useRef(null);
  const hiddenInputRef = useRef(null);
  const isFocusedRef = useRef(false);

  const [inputText, setInputText] = useState(() => getDisplayDate(value));
  const [internalError, setInternalError] = useState("");

  const validateDateString = (dateStr) => {
    if (!dateStr || dateStr.trim() === "") return "";
    if (dateStr.length < 10) return "Please enter a valid date (DD/MM/YYYY)";
    const m = moment(dateStr, "DD/MM/YYYY", true);
    if (!m.isValid()) return "Invalid date";
    const y = m.year();
    if (y < 1900 || y > 2099) return "Please enter a valid year (1900-2099)";
    if (min && m.isBefore(moment(getYmdDate(min), "YYYY-MM-DD"), "day")) {
      return `Date cannot be before ${getDisplayDate(min)}`;
    }
    if (max && m.isAfter(moment(getYmdDate(max), "YYYY-MM-DD"), "day")) {
      return `Date cannot be after ${getDisplayDate(max)}`;
    }
    return "";
  };

  useEffect(() => {
    if (!isFocusedRef.current) {
      const disp = getDisplayDate(value);
      setInputText(disp);
      if (disp && disp.length === 10) {
        setInternalError(validateDateString(disp));
      } else {
        setInternalError("");
      }
    }
  }, [value]);

  const handleDateKeyDown = (eKey, currentText, selStart, selEnd) => {
    let val = currentText || "";
    let start = selStart;
    let end = selEnd;

    let parts = val.includes("/") ? val.split("/") : [val, "", ""];
    while (parts.length < 3) parts.push("");
    let [d, m, y] = parts;

    // Determine segment: 0 = day, 1 = month, 2 = year
    let seg = 0;
    if (start >= 6 || (parts[0] && parts[1] && start > (parts[0].length + parts[1].length + 1))) {
      seg = 2;
    } else if (start >= 3 || (parts[0] && start > parts[0].length)) {
      seg = 1;
    }

    // Handle Slash '/'
    if (eKey === "/") {
      if (seg === 0) {
        d = d ? String(parseInt(d, 10) || 1).padStart(2, "0") : "01";
        let nextVal = d + "/" + (m || "") + (y ? "/" + y : "");
        return { val: nextVal, sel: m ? [3, 5] : [3, 3] };
      } else if (seg === 1) {
        m = m ? String(parseInt(m, 10) || 1).padStart(2, "0") : "01";
        let nextVal = (d || "01") + "/" + m + "/" + (y || "");
        return { val: nextVal, sel: y ? [6, 10] : [6, 6] };
      }
      return { val, sel: [start, end] };
    }

    // Handle ArrowRight
    if (eKey === "ArrowRight") {
      if (seg === 0 && start >= (d ? d.length : 0)) return { val, sel: m ? [3, 5] : [3, 3] };
      if (seg === 1 && start >= (d ? d.length + 1 + (m ? m.length : 0) : 5)) return { val, sel: y ? [6, 10] : [6, 6] };
    }

    // Handle ArrowLeft
    if (eKey === "ArrowLeft") {
      if (seg === 2 && start <= 6) return { val, sel: m ? [3, 5] : [3, 3] };
      if (seg === 1 && start <= 3) return { val, sel: d ? [0, 2] : [0, 0] };
    }

    // Handle Backspace
    if (eKey === "Backspace") {
      if (seg === 2 && start === 6 && end === 6) {
        return { val, sel: m ? [3, 5] : [3, 3] };
      }
      if (seg === 1 && start === 3 && end === 3) {
        return { val, sel: d ? [0, 2] : [0, 0] };
      }
    }

    // Handle digits
    if (/^[0-9]$/.test(eKey)) {
      if (seg === 0) {
        // Day segment
        let isFullSelect = start === 0 && end >= 2;
        if (isFullSelect || start === 0) {
          if (eKey >= "4") {
            d = "0" + eKey;
            let nextVal = d + "/" + (m || "") + (y ? "/" + y : "");
            return { val: nextVal, sel: m ? [3, 5] : [3, 3] };
          } else {
            d = eKey;
            let nextVal = d + (m || y ? "/" + (m || "") + (y ? "/" + y : "") : "");
            return { val: nextVal, sel: [1, 1] };
          }
        } else {
          // Typing second digit of Day
          d = (d[0] || "0") + eKey;
          let num = parseInt(d, 10);
          if (num === 0) num = 1;
          if (num > 31) num = 31;
          d = String(num).padStart(2, "0");
          let nextVal = d + "/" + (m || "") + (y ? "/" + y : "");
          return { val: nextVal, sel: m ? [3, 5] : [3, 3] };
        }
      } else if (seg === 1) {
        // Month segment
        let isFullSelect = start <= 3 && end >= 5;
        if (isFullSelect || start === 3) {
          if (eKey >= "2") {
            m = "0" + eKey;
            let nextVal = (d || "01") + "/" + m + (y ? "/" + y : "");
            return { val: nextVal, sel: y ? [6, 10] : [6, 6] };
          } else {
            m = eKey;
            let nextVal = (d || "01") + "/" + m + (y ? "/" + y : "");
            return { val: nextVal, sel: [4, 4] };
          }
        } else {
          // Typing second digit of Month
          m = (m[0] || "0") + eKey;
          let num = parseInt(m, 10);
          if (num === 0) num = 1;
          if (num > 12) num = 12;
          m = String(num).padStart(2, "0");
          let nextVal = (d || "01") + "/" + m + "/" + (y || "");
          return { val: nextVal, sel: y ? [6, 10] : [6, 6] };
        }
      } else {
        // Year segment
        let isFullSelect = start <= 6 && end >= 10;
        if (isFullSelect || start === 6) {
          y = eKey;
          let nextVal = (d || "01") + "/" + (m || "01") + "/" + y;
          return { val: nextVal, sel: [7, 7] };
        } else {
          y = (y || "") + eKey;
          if (y.length > 4) y = y.slice(0, 4);
          let nextVal = (d || "01") + "/" + (m || "01") + "/" + y;
          let nextPos = 6 + y.length;
          return { val: nextVal, sel: [nextPos, nextPos] };
        }
      }
    }

    return null;
  };

  const handleKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) {
      if (inputProps?.onKeyDown) inputProps.onKeyDown(e);
      if (InputProps?.onKeyDown) InputProps.onKeyDown(e);
      return;
    }

    const start = inputRef.current ? inputRef.current.selectionStart : 0;
    const end = inputRef.current ? inputRef.current.selectionEnd : 0;
    const res = handleDateKeyDown(e.key, inputText, start, end);

    if (res) {
      e.preventDefault();
      setInputText(res.val);

      if (res.val.length === 10) {
        const err = validateDateString(res.val);
        setInternalError(err);
        if (typeof onChange === "function") {
          const m = moment(res.val, "DD/MM/YYYY", true);
          if (!err && m.isValid()) {
            onChange({ target: { name: name || id, value: m.format("YYYY-MM-DD"), isValid: true } });
          } else {
            onChange({ target: { name: name || id, value: res.val, isValid: false, isInvalid: true } });
          }
        }
      } else {
        setInternalError("");
        if (typeof onChange === "function") {
          onChange({ target: { name: name || id, value: res.val, isValid: false, isInvalid: true } });
        }
      }

      if (res.sel && inputRef.current) {
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(res.sel[0], res.sel[1]);
          }
        }, 0);
      }
    }

    if (inputProps?.onKeyDown) inputProps.onKeyDown(e);
    if (InputProps?.onKeyDown) InputProps.onKeyDown(e);
  };

  const handleMouseUp = () => {
    const el = inputRef.current;
    if (!el) return;
    const val = el.value || "";
    if (val.length === 10 && val.includes("/")) {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      if (start === end) {
        if (start <= 2) {
          el.setSelectionRange(0, 2);
        } else if (start <= 5) {
          el.setSelectionRange(3, 5);
        } else {
          el.setSelectionRange(6, 10);
        }
      }
    }
  };

  const handleTextChange = (e) => {
    const raw = e.target.value;
    const isDeleting =
      (e.nativeEvent && (e.nativeEvent.inputType === "deleteContentBackward" || e.nativeEvent.inputType === "deleteContentForward")) ||
      raw.length < inputText.length;

    if (!raw || raw.trim() === "") {
      setInputText("");
      setInternalError("");
      if (typeof onChange === "function") {
        onChange({ target: { name: name || id, value: "", isValid: true } });
      }
      return;
    }

    const formatted = formatDdMmYyyy(raw, isDeleting);
    setInputText(formatted);

    if (formatted.length === 10) {
      const err = validateDateString(formatted);
      setInternalError(err);
      if (typeof onChange === "function") {
        const m = moment(formatted, "DD/MM/YYYY", true);
        if (!err && m.isValid()) {
          onChange({
            target: { name: name || id, value: m.format("YYYY-MM-DD"), isValid: true },
          });
        } else {
          onChange({
            target: { name: name || id, value: formatted, isValid: false, isInvalid: true },
          });
        }
      }
    } else {
      // Incomplete/partial date while typing
      setInternalError("");
      if (typeof onChange === "function") {
        onChange({
          target: { name: name || id, value: formatted, isValid: false, isInvalid: true },
        });
      }
    }
  };

  const handleBlur = (e) => {
    isFocusedRef.current = false;

    if (!inputText || inputText.trim() === "") {
      setInputText("");
      setInternalError("");
      if (typeof onChange === "function") {
        onChange({ target: { name: name || id, value: "", isValid: true } });
      }
    } else {
      const err = validateDateString(inputText);
      setInternalError(err);
      if (!err) {
        const m = moment(inputText, "DD/MM/YYYY", true);
        const formatted = m.format("DD/MM/YYYY");
        setInputText(formatted);
        if (typeof onChange === "function") {
          onChange({ target: { name: name || id, value: m.format("YYYY-MM-DD"), isValid: true } });
        }
      } else {
        if (typeof onChange === "function") {
          onChange({ target: { name: name || id, value: inputText, isValid: false, isInvalid: true } });
        }
      }
    }

    if (inputProps?.onBlur) inputProps.onBlur(e);
    if (InputProps?.onBlur) InputProps.onBlur(e);
  };

  const handleNativePickerChange = (e) => {
    const ymd = e.target.value;
    if (ymd) {
      const m = moment(ymd, "YYYY-MM-DD", true);
      if (m.isValid()) {
        const dmy = m.format("DD/MM/YYYY");
        setInputText(dmy);
        const err = validateDateString(dmy);
        setInternalError(err);
        if (typeof onChange === "function") {
          onChange({
            target: {
              name: name || id,
              value: !err ? m.format("YYYY-MM-DD") : dmy,
              isValid: !err,
              isInvalid: Boolean(err),
            },
          });
        }
      }
    }
  };

  const handleOpenPicker = (e) => {
    if (e) e.stopPropagation();
    if (hiddenInputRef.current) {
      if (typeof hiddenInputRef.current.showPicker === "function") {
        hiddenInputRef.current.showPicker();
      } else {
        hiddenInputRef.current.click();
      }
    }
  };

  const isShrunk =
    InputLabelProps && InputLabelProps.shrink !== undefined
      ? InputLabelProps.shrink
      : (label ? true : Boolean(inputText));

  const effectivePlaceholder =
    placeholder !== undefined
      ? placeholder
      : "DD/MM/YYYY";

  return (
    <div style={{ position: "relative", width: fullWidth ? "100%" : "auto", ...style }}>
      <TextField
        inputRef={inputRef}
        fullWidth={fullWidth}
        id={id}
        name={name}
        label={label}
        variant={variant}
        size={size}
        margin={margin}
        disabled={disabled}
        required={required}
        className={`${classes.dateFieldRoot} dd-mm-yyyy-date-field ${className}`}
        value={inputText}
        placeholder={effectivePlaceholder}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        onMouseUp={handleMouseUp}
        onFocus={(e) => {
          isFocusedRef.current = true;
          if (inputProps?.onFocus) inputProps.onFocus(e);
          if (InputProps?.onFocus) InputProps.onFocus(e);
        }}
        onBlur={handleBlur}
        error={Boolean(error) || Boolean(internalError)}
        helperText={helperText || internalError}
        FormHelperTextProps={FormHelperTextProps}
        InputLabelProps={{
          shrink: isShrunk,
          ...InputLabelProps,
        }}
        inputProps={{
          maxLength: 10,
          ...inputProps,
        }}
        InputProps={{
          endAdornment: (
            <IconButton
              size="small"
              onClick={handleOpenPicker}
              style={{ padding: 4 }}
              tabIndex={-1}
              aria-label="Open calendar"
            >
              <CalendarTodayIcon fontSize="small" style={{ color: "#036dda" }} />
            </IconButton>
          ),
          ...InputProps,
        }}
      />
      <input
        ref={hiddenInputRef}
        type="date"
        value={getYmdDate(value)}
        min={getYmdDate(min)}
        max={getYmdDate(max)}
        onChange={handleNativePickerChange}
        style={{
          position: "absolute",
          left: 0,
          bottom: 0,
          width: "100%",
          height: "1px",
          opacity: 0,
          pointerEvents: "none",
          zIndex: -1,
        }}
        tabIndex={-1}
      />
    </div>
  );
};

export default DdMmYyyyDateField;
